import { Link, useParams } from "react-router-dom";
import ProductList from "../components/ProductList";
import {
  capitalizeFirstLetter,
  countSelectedFilters,
  EMPTY_PRODUCT_FILTERS,
  filterProducts,
  PRICE_RANGES,
  ProductFilterSelection,
  sortProducts,
} from "../extensions/helpers";
import ProductFilters, {
  FilterFacet,
  FilterOption,
} from "../components/ProductFilters";
// import { useSearch } from "../contexts/SearchContext";
import { useSelectedLocation } from "../contexts/SelectedLocationContext";
import { useEffect, useMemo, useState } from "react";
import { UrlParams } from "../types/Params";
import FilterSort from "../components/FilterSort";
import { SortBy } from "../types/SortBy";
import PageContainer, { GapSize } from "../components/PageContainer";
import { ProductGridSkeleton } from "../components/ProductCardSkeleton";
import { useCatalog } from "../contexts/CatalogContext";
import { findOrGetItemsByCategory } from "../api";
import { useDocumentMeta } from "../hooks/useDocumentMeta";

const ProductLinePage = () => {
  // All product-related pages end up in this component, so all deep links go through here
  // Set all the params when loading any page
  const { domain, category, product } = useParams<UrlParams>();
  // const { searchQuery } = useSearch();
  const { setDomain, setCategory, setProduct } = useSelectedLocation();
  const {
    categories,
    getCategoryByName,
    getItemsByCategory,
    getCategoryById,
    getChildCategories,
    colorOption,
    sizeOption,
    addItems,
    addImages,
    getImagesByIds,
  } = useCatalog();

  const [sort, setSort] = useState<SortBy>(SortBy.Featured);
  const [isFetchingItems, setIsFetchingItems] = useState<boolean>(false);
  const [isFilterOpen, setIsFilterOpen] = useState<boolean>(false);
  const [filters, setFilters] = useState<ProductFilterSelection>(
    EMPTY_PRODUCT_FILTERS,
  );

  // When Category isn't selected, coalesce for non-parented categories (domains)
  const domainObject = useMemo(
    () => getCategoryByName(domain ?? ""),
    [domain, getCategoryByName],
  );
  const categoryObject = useMemo(
    () => getCategoryByName(category ?? domain ?? ""),
    [category, domain, getCategoryByName],
  );

  useDocumentMeta({
    title:
      capitalizeFirstLetter(category) ??
      capitalizeFirstLetter(domain) ??
      undefined,
    description: `Shop ${category ?? domain ?? "our collection"} at The Americana Company — classic Americana clothing and lifestyle goods.`,
  });

  const lineItems = getItemsByCategory(categoryObject?.id ?? "");

  // Narrow first, then sort: sorting the full list and filtering after would
  // give the same order, but "N Results" has to count what's actually shown.
  const filteredLineItems = useMemo(
    () => filterProducts(lineItems, filters),
    [lineItems, filters],
  );
  const sortedLineItems = useMemo(
    () => sortProducts(filteredLineItems, undefined, sort, categoryObject?.id),
    [filteredLineItems, sort, categoryObject?.id],
  );

  // Facet values come from the items on THIS page, in Square's own option-value
  // order — the merchant already arranged sizes XS→3XL there, and duplicating
  // that as a hardcoded rank in the app is a second source of truth that goes
  // stale the first time a size is added.
  const facets: FilterFacet[] = useMemo(() => {
    const presentIds = new Set(
      lineItems.flatMap((item) =>
        (item.itemData?.variations ?? []).flatMap((v) =>
          (v.itemVariationData?.itemOptionValues ?? []).map(
            (o) => o.itemOptionValueId ?? "",
          ),
        ),
      ),
    );

    const optionsFor = (
      option: typeof colorOption,
      withColor: boolean,
    ): FilterOption[] =>
      (option?.itemOptionData?.values ?? [])
        .filter((v) => presentIds.has(v.id ?? ""))
        .map((v) => ({
          id: v.id ?? "",
          name: v.itemOptionValueData?.name ?? "",
          color: withColor
            ? (v.itemOptionValueData?.color ?? undefined)
            : undefined,
        }));

    const sizeOptions = optionsFor(sizeOption, false);
    const colorOptions = optionsFor(colorOption, true);

    // A price bucket nothing falls into is a dead end, so only offer the ones
    // this category actually spans.
    const priceOptions: FilterOption[] = PRICE_RANGES.filter((r) =>
      lineItems.some((item) => {
        const cents = Number(
          item.itemData?.variations?.[0]?.itemVariationData?.priceMoney?.amount,
        );
        return (
          Number.isFinite(cents) && cents >= r.minCents && cents <= r.maxCents
        );
      }),
    ).map((r) => ({ id: r.id, name: r.name }));

    // A facet with one value can't narrow anything — drop it rather than
    // showing a checkbox that always returns the same list.
    return [
      { key: "sizeIds" as const, label: "Size", options: sizeOptions },
      { key: "colorIds" as const, label: "Color", options: colorOptions },
      { key: "priceRangeIds" as const, label: "Price", options: priceOptions },
    ].filter((f) => f.options.length > 1);
  }, [lineItems, sizeOption, colorOption]);

  const selectedFilterCount = countSelectedFilters(filters);

  const toggleFilter = (
    key: keyof ProductFilterSelection,
    optionId: string,
  ) => {
    setFilters((prev) => {
      const current = prev[key];
      return {
        ...prev,
        [key]: current.includes(optionId)
          ? current.filter((id) => id !== optionId)
          : [...current, optionId],
      };
    });
  };

  // Facet ids are per-category, so a selection carried across categories would
  // silently match nothing. Reset when the category changes.
  useEffect(() => {
    setFilters(EMPTY_PRODUCT_FILTERS);
  }, [categoryObject?.id]);

  useEffect(() => {
    setDomain(domain ?? null);
    setCategory(category ?? null);
    setProduct(product ?? null);
  }, [domain, category, product, setDomain, setCategory, setProduct]);

  useEffect(() => {
    const fetchItems = async () => {
      setIsFetchingItems(true);
      try {
        await findOrGetItemsByCategory(
          categoryObject?.id ?? "",
          addItems,
          getItemsByCategory,
          addImages,
          getImagesByIds,
        );
      } catch (error) {
        console.error("Get items failed:", error);
      } finally {
        setIsFetchingItems(false);
      }
    };

    // Fetch items if category items haven't been fetched
    if (!!categoryObject?.id) {
      fetchItems();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [domainObject, categoryObject]);

  // Sibling categories, shown as pills so a shopper can move between T-Shirts,
  // Hats and so on without going back up to the nav. On a domain page
  // (`/shop/apparel`) `categoryObject` IS the domain, so its own children are
  // the right set; on a category page they come from its parent.
  const pillParentId =
    getCategoryById(categoryObject?.categoryData?.parentCategory?.id ?? "")
      ?.id ?? categoryObject?.id;
  const pillCategories = getChildCategories(pillParentId ?? "");

  return (
    <PageContainer hasPadding gapSize={GapSize.Tiny}>
      <div className="browse-header">
        <nav className="browse-breadcrumb" aria-label="Breadcrumb">
          <Link to="/home">Home</Link>
          {!!domain && (
            <>
              <span className="sep" aria-hidden="true">
                /
              </span>
              {category ? (
                <Link to={`/shop/${domain}`}>
                  {capitalizeFirstLetter(domain)}
                </Link>
              ) : (
                <span aria-current="page">{capitalizeFirstLetter(domain)}</span>
              )}
            </>
          )}
          {!!category && (
            <>
              <span className="sep" aria-hidden="true">
                /
              </span>
              <span aria-current="page">{capitalizeFirstLetter(category)}</span>
            </>
          )}
        </nav>

        {/* Square's own casing ("T-Shirts") beats the lowercased URL segment
            capitalizeFirstLetter would give back ("T-shirts"). */}
        <h1 className="browse-title">
          {categoryObject?.categoryData?.name ??
            capitalizeFirstLetter(category) ??
            capitalizeFirstLetter(domain)}
        </h1>

        {pillCategories.length > 1 && (
          <div className="browse-pills">
            {pillCategories.map((c) => {
              const name = c.categoryData?.name ?? "";
              const isActive =
                name.toLowerCase() === (category ?? "").toLowerCase();
              return (
                <Link
                  key={c.id}
                  to={`/shop/${domain}/category/${encodeURIComponent(
                    name.toLowerCase(),
                  )}`}
                  className={`browse-pill ${isActive ? "browse-pill--active" : ""}`}
                  aria-current={isActive ? "page" : undefined}
                >
                  {name}
                </Link>
              );
            })}
          </div>
        )}
      </div>

      <FilterSort
        sort={sort}
        setSort={setSort}
        productCount={sortedLineItems?.length}
        isFilterOpen={isFilterOpen}
        onToggleFilter={() => setIsFilterOpen((open) => !open)}
        selectedFilterCount={selectedFilterCount}
        canFilter={facets.length > 0}
      />

      <div className="browse-body">
        {isFilterOpen && facets.length > 0 && (
          <ProductFilters
            facets={facets}
            selection={filters}
            onToggle={toggleFilter}
            onClear={() => setFilters(EMPTY_PRODUCT_FILTERS)}
            onClose={() => setIsFilterOpen(false)}
            selectedCount={selectedFilterCount}
          />
        )}

        <div className="browse-results">
          {/* Skeleton while the catalog itself or this category's items load */}
          {(categories.length === 0 || isFetchingItems) &&
          sortedLineItems.length === 0 ? (
            <ProductGridSkeleton />
          ) : sortedLineItems.length === 0 && selectedFilterCount > 0 ? (
            <p className="browse-empty">
              Nothing matches those filters.{" "}
              <button
                type="button"
                className="underline"
                onClick={() => setFilters(EMPTY_PRODUCT_FILTERS)}
              >
                Clear them
              </button>{" "}
              to see everything in this category.
            </p>
          ) : (
            // On a domain page `categoryObject` is the TOP-LEVEL category —
            // right for fetching items, wrong for building product links.
            // ProductCard treats both props as a fallback behind the item's own
            // lowest category, so passing them here is safe.
            <ProductList
              domain={domainObject}
              category={categoryObject}
              items={sortedLineItems}
            />
          )}
        </div>
      </div>
    </PageContainer>
  );
};

export default ProductLinePage;
